import type { ReactNode } from 'react';
import classnames from 'classnames/bind';
import { isFunction, isNil } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import { Button } from 'app/components/core/Button';
import { useTheme } from 'app/components/core/ThemeProvider';
import * as styles from './Spinner.css';

const cx = classnames.bind(styles);

export interface SpinnerProps {
    children?: ReactNode;
    testId?: string;
    className?: string;
    clickThroughBackground?: boolean;
    size?: LoadingSize | null;
    onCancel?: (() => void) | null;
}

export function Spinner({
    children,
    testId = 'spinner',
    className,
    clickThroughBackground = false,
    size = null,
    onCancel = null
}: SpinnerProps) {
    const { t } = useTranslation();
    const { themeClassname } = useTheme();
    const classes = cx('base', className, themeClassname, {
        'click-through-background': clickThroughBackground
    });

    return (
        <div data-testid={testId} className={classes}>
            {isNil(onCancel) && (
                <Loading className={styles.loading} size={size ?? LoadingSize.EXTRA_LARGE} />
            )}
            {children}
            {isFunction(onCancel) && (
                <Button className={styles.cancel} onClick={onCancel}>
                    <Loading size={LoadingSize.SMALL} />
                    {t('common:cancel')}
                </Button>
            )}
        </div>
    );
}
